import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { CheckCircle2, Search as SearchIcon, X, ChevronRight } from "lucide-react";

const s = (v, fb = "—") => (v == null || v === "" ? fb : (typeof v === "object" ? fb : v));
const peso = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { maximumFractionDigits: 0 });

const stageLabel = (l) => l.converted ? (l.stage || "Converted") : l.status;
const stageClass = (l) => {
    if (!l.converted) {
        if (l.status === "Qualified") return "text-sky-600 bg-sky-500/10";
        if (l.status === "Not Interested") return "text-rose-600 bg-rose-500/10";
        return "text-muted-foreground bg-secondary";
    }
    if (l.stage === "Closed Won") return "text-emerald-600 bg-emerald-500/10";
    if (l.stage === "Closed Lost") return "text-rose-600 bg-rose-500/10";
    return "text-amber-600 bg-amber-500/10";
};

export default function AgentStats() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [q, setQ] = useState("");
    const [sortKey, setSortKey] = useState("earned_total");
    const [drill, setDrill] = useState(null); // agent name

    useEffect(() => {
        (async () => {
            setLoading(true); setErrored(false);
            try {
                const { data } = await api.get("/crm/agent-stats");
                setData(data && typeof data === "object" ? data : null);
            } catch (e) { setErrored(true); toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    const agents = Array.isArray(data?.agents) ? data.agents : [];
    const totals = data?.totals || {};
    const filtered = agents
        .filter((a) => !q || String(a.agent).toLowerCase().includes(q.toLowerCase()))
        .sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));

    const th = (key, label, extra = "") => (
        <th
            className={`cursor-pointer select-none ${extra}`}
            onClick={() => setSortKey(key)}
            title="Sort by this column"
        >
            {label}{sortKey === key ? " ↓" : ""}
        </th>
    );

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="crm-agent-stats-page">
            <div>
                <div className="sc-section-title">CRM</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Agent Stats</h1>
                <p className="text-sm text-muted-foreground mt-1">Leads per agent, their stage breakdown, and value earned per lead.</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card label="Agents" value={loading ? "…" : (data?.agent_count ?? agents.length)} />
                <Card label="Total Leads" value={loading ? "…" : (totals.leads ?? 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
                <Card label="Converted" value={loading ? "…" : (totals.converted ?? 0).toLocaleString("en-PH", { timeZone: "Asia/Manila" })} />
                <Card label="Total Earned" value={loading ? "…" : peso(totals.earned)} />
            </div>

            <div className="sc-card p-3">
                <div className="relative max-w-sm">
                    <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="sc-input pl-9" placeholder="Filter agents…" value={q} onChange={(e) => setQ(e.target.value)} />
                </div>
            </div>

            <div className="sc-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full sc-table min-w-[1050px]">
                        <thead>
                            <tr>
                                <th>Agent</th>
                                {th("leads_total", "Leads", "text-right")}
                                {th("new", "New", "text-right")}
                                {th("qualified", "Qualified", "text-right")}
                                {th("converted", "Converted", "text-right")}
                                {th("won", "Won", "text-right")}
                                {th("open_deals", "In Pipeline", "text-right")}
                                {th("earned_total", "Earned", "text-right")}
                                {th("earned_per_lead", "Earned / Lead", "text-right")}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                            {!loading && errored && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">Couldn't load agent stats.</td></tr>}
                            {!loading && !errored && filtered.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">No agents.</td></tr>}
                            {!loading && !errored && filtered.map((a, i) => (
                                <tr
                                    key={a.agent || i}
                                    className="cursor-pointer group hover:bg-secondary/40"
                                    onClick={() => setDrill(a.agent)}
                                    data-testid={`agent-stats-row-${i}`}
                                >
                                    <td>
                                        <div className="font-medium flex items-center gap-1.5">
                                            {s(a.agent)}
                                            {a.matched_user && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" title={`CRM: ${a.matched_user.email}`} />}
                                            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                                        </div>
                                        <div className="text-xs text-muted-foreground">{s(a.store, "")}</div>
                                    </td>
                                    <td className="sc-num font-semibold">{a.leads_total}</td>
                                    <td className="sc-num text-muted-foreground">{a.new}</td>
                                    <td className="sc-num">{a.qualified}</td>
                                    <td className="sc-num">{a.converted}</td>
                                    <td className="sc-num text-emerald-600">{a.won}</td>
                                    <td className="sc-num text-sky-600">{a.open_deals}</td>
                                    <td className="sc-num font-semibold">{peso(a.earned_total)}</td>
                                    <td className="sc-num">{peso(a.earned_per_lead)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <p className="text-xs text-muted-foreground">
                Click an agent row to drill into their leads. "Earned" = each lead's Sales Amount (from the Lark import). Stages: New / Qualified are open leads; Converted leads become pipeline deals (Won or still In Pipeline). Click a column header to sort. For real invoiced revenue see Team Performance → Sales (SAP).
            </p>

            {drill && <AgentDrill agent={drill} onClose={() => setDrill(null)} />}
        </div>
    );
}

function AgentDrill({ agent, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [stageFilter, setStageFilter] = useState("all");

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true); setErrored(false);
            try {
                const { data } = await api.get("/crm/agent-stats/leads", { params: { agent } });
                if (alive) setData(data && typeof data === "object" ? data : null);
            } catch (e) { if (alive) setErrored(true); }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [agent]);

    const leads = Array.isArray(data?.leads) ? data.leads : [];
    const buckets = leads.reduce((m, l) => { const k = stageLabel(l); m[k] = (m[k] || 0) + 1; return m; }, {});
    const shown = stageFilter === "all" ? leads : leads.filter((l) => stageLabel(l) === stageFilter);

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-end z-50" onClick={onClose}>
            <div className="w-full max-w-3xl h-full bg-background overflow-y-auto border-l border-border" onClick={(e) => e.stopPropagation()} data-testid="agent-drill-drawer">
                <div className="px-4 py-3 border-b border-border sticky top-0 bg-background z-10">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground">Agent Drill-down</div>
                            <div className="text-lg font-semibold">{s(agent)}</div>
                        </div>
                        <button onClick={onClose} className="p-1 rounded hover:bg-secondary text-muted-foreground"><X className="w-4 h-4" /></button>
                    </div>
                    {!loading && data && (
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <div className="rounded-md border border-border px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Leads</div><div className="text-sm font-semibold">{data.count}</div></div>
                            <div className="rounded-md border border-border px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Earned</div><div className="text-sm font-semibold">{peso(data.earned_total)}</div></div>
                        </div>
                    )}
                    {!loading && leads.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                            <Chip label={`All (${leads.length})`} active={stageFilter === "all"} onClick={() => setStageFilter("all")} />
                            {Object.entries(buckets).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                                <Chip key={k} label={`${k} (${n})`} active={stageFilter === k} onClick={() => setStageFilter(k)} />
                            ))}
                        </div>
                    )}
                </div>

                <div className="p-4">
                    {loading && <div className="text-center text-sm text-muted-foreground py-10">Loading…</div>}
                    {!loading && errored && <div className="text-center text-sm text-muted-foreground py-10">Couldn't load this agent's leads.</div>}
                    {!loading && !errored && shown.length === 0 && <div className="text-center text-sm text-muted-foreground py-10">No leads.</div>}
                    {!loading && !errored && shown.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full sc-table">
                                <thead><tr><th>Company / Contact</th><th>Stage</th><th className="text-right">Earned</th><th>Date</th></tr></thead>
                                <tbody>
                                    {shown.map((l) => (
                                        <tr key={l.id} className={l.deal_id ? "cursor-pointer" : ""} onClick={() => l.deal_id && window.location.assign(`/crm/deals/${l.deal_id}`)}>
                                            <td>
                                                <div className="text-sm font-medium">{s(l.company_name, l.contact || "—")}</div>
                                                <div className="text-xs text-muted-foreground">{[l.contact, l.store].filter(Boolean).join(" · ")}</div>
                                            </td>
                                            <td><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${stageClass(l)}`}>{stageLabel(l)}</span></td>
                                            <td className="sc-num">{l.earned ? peso(l.earned) : "—"}</td>
                                            <td className="text-xs text-muted-foreground whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Chip({ label, active, onClick }) {
    return (
        <button onClick={onClick} className={`px-2 py-0.5 rounded-full text-xs border ${active ? "bg-secondary border-border font-medium" : "border-border text-muted-foreground hover:bg-secondary/50"}`}>{label}</button>
    );
}

function Card({ label, value }) {
    return (
        <div className="sc-card p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-lg font-semibold mt-1">{value}</div>
        </div>
    );
}
